import { Trans, useTranslation } from "react-i18next";
import SopotakContext from "../../store/sopotak-context";
import "bootstrap/dist/css/bootstrap.min.css";
import config from "../../api/api-helper";
import { Col, Container, Form, Row, FloatingLabel } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import "../../styles.css";
import { getFullPhoneNumber } from "../../utils/phone";
import { DateTime } from "luxon";

import React, { useState, useContext, useEffect } from "react";
import "react-phone-number-input/style.css";
import BoatTermsModal from "./contact/BoatTermsModal";
import "./contact/contactPane.css";
import CountrySelect from "./contact/CountrySelect";
import plLabels from "react-phone-number-input/locale/pl.json";
import enLabels from "react-phone-number-input/locale/en.json";

import { parsePhoneNumberFromString } from "libphonenumber-js";

const SummaryAndContact = (props) => {
  const { t, i18n } = useTranslation();
  const ctx = useContext(SopotakContext);

  const {
    invoiceRequested,
    companyName,
    nip,
    city,
    postalCode,
    streetAddress,
    apartmentNumber,
    offersData,
    departureOffer,
    returnOffer,
    firstName,
    lastName,
    email,
    phoneNumber,
    countryCode,
    persons,
    children,
    bikes,
    comments,
    recommendation,
    marketingInfoApproved,
    tripType,
    from,
    to,
  } = useContext(SopotakContext);

  const [discountCode, setDiscountCode] = useState("");
  const [discountData, setDiscountData] = useState(null);

  const labels = i18n.language.startsWith("pl") ? plLabels : enLabels;

  const [invoice, setInvoice] = useState(false);
  const [invoiceCountry, setInvoiceCountry] = useState("PL");

  const [validated, setValidated] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [approvalChecked, setApprovalChecked] = useState(false);
  const [boatTermsConfirmed, setBoatTermsConfirmed] = useState(false);

  const [show, setShow] = useState(false);

  const locale = `${i18n.language}-${i18n.language.toUpperCase()}`;


  useEffect(() => {
    if (!ctx.countryCode) {
      const defaultCode = i18n.language.startsWith("pl") ? "PL" : "GB";
      ctx.setCountryCode(defaultCode);
    }
  }, [countryCode, i18n.language]);

  const handleClose = () => {
    setBoatTermsConfirmed(false);
    setShow(false);
  };
  const handleConfirm = () => {
    setBoatTermsConfirmed(true);
    setShow(false);
  };

  const handleShow = () => setShow(true);
  
  const getInvoiceData = () => {
    if (!invoiceRequested) return null;
    return {
      companyName,
      nip,
      city,
      postalCode,
      streetAddress,
      apartmentNumber,
    };
  };

  const buildBookingBody = (discountCode = "", discountData = null) => {
    const invoiceData = getInvoiceData();
    return {
      departureLocation: from,
      arrivalLocation: to,
      type: tripType,
      departureOffer: discountData?.departureOffer || departureOffer,
      returnOffer: discountData?.returnOffer || returnOffer,
      customerInformation: {
        firstName,
        lastName,
        email,
        phoneNumber: getFullPhoneNumber(countryCode, phoneNumber),
        passengerDetails: {
          numberOfPersons: persons,
          numberOfChildren: children,
          numberOfBikes: bikes,
        },
        invoice: invoiceData,
      },
      additionalInformation: {
        comments,
        recommendation,
        marketingInfoApproved,
        discountCode,
      },
    };
  };

  

  const handleResponse = async (response) => {
    const data = await response.json();
    if (response.ok) {
      return data;
    } else if (response.status === 409) {
      throw new Error("Duplicate booking detected");
    } else if (response.status === 404 && data.message) {
      throw new Error(data.message);
    } else {
      throw new Error(
        data.message || "An error occurred while processing the request"
      );
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const phoneValid = isPhoneNumberValidCustom();
    const emailValid = isEmailValid(ctx.email);
    const form = event.currentTarget;

    const isNipProvided = ctx.nip && ctx.nip.trim().length > 0;
    const nipValid = !invoice || (isNipProvided && (invoiceCountry === "PL" ? isValidNip(ctx.nip) : true));
    if (
      form.checkValidity() === false ||
      !phoneValid ||
      !emailValid ||
      !nipValid ||
      !termsChecked ||
      !approvalChecked ||
      !boatTermsConfirmed
    ) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);

    const body = buildBookingBody(discountCode, discountData);

    try {
      const response = await fetch(`${config.apiUrl}/api/sopotak/v1/bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept-Language": locale,
        },
        body: JSON.stringify(body),
      });

      const data = await handleResponse(response);
      window.location.href = data.paymentUrl;
    } catch (error) {
      console.error("Error:", error.message);
      alert(
        error.message === "Duplicate booking detected"
          ? t("summary.duplicateError")
          : t("summary.genericError")
      );
    }
  };

  const handleInvoiceCheckboxChange = (event) => {
    ctx.setInvoice(!invoice);
    setInvoice(!invoice);
  };

  const handleInvoiceCountryChange = (event) => {
    setInvoiceCountry(event.target.value);
  };

  const handleTermsChange = (event) => {
    setTermsChecked(event.target.checked);
  };

  const handleApproveChange = (event) => {
    setApprovalChecked(event.target.checked);
  };

  const handleMarketingChange = (event) => {
    ctx.setMarketingInfoApproved(event.target.checked);
  };

  const handleBoatTermsConfirmedChange = (event) => {
    if (event.target.checked) {
      handleShow();
    }
    setBoatTermsConfirmed(event.target.checked);
  };

  const recommendationChangeHandler = (event) => {
    ctx.setRecommendation(event.target.value);
  };

  function isValidNip(nip) {
    if (!nip) return false;
    
    const cleanNip = String(nip).replace(/[\ \-]/g, "");
    
    if (cleanNip.length !== 10) return false;
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanNip[i], 10) * weights[i];
    }
    const result = sum % 11;
    const checksum = result === 10 ? 0 : result;
    const lastDigit = parseInt(cleanNip[9], 10);
    return checksum === lastDigit;
  }

  function isEmailValid(email) {
    var reg = /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,4})$/;
    if (!reg.test(email)) return false;
    else return true;
  }

  const isPhoneNumberValidCustom = () => {
    if (!ctx.countryCode || !ctx.phoneNumber) return false;

    const full = getFullPhoneNumber(ctx.countryCode, ctx.phoneNumber);
    const parsed = parsePhoneNumberFromString(full);

    return parsed?.isValid() || false;
  };

  function getPostalCodeErrorMessage() {
    return ctx.postalCode.length === 0
      ? t("contact.required")
      : ctx.postalCode.length < 5
      ? t("contact.postalTooShort")
      : ctx.postalCode.length > 6
      ? t("contact.postalTooLong")
      : "";
  }

  const handlePhoneNumberChange = (value) => {
    ctx.setPhoneNumber(value);
  };

  const handleCountryChange = (value) => {
    ctx.setCountryCode(value);
  };

  function navigateToOrderPage() {
    ctx.setOffersData(null);
    props.progressHandler(1);
  }

  const submitDiscountCodeHandler = (event) => {
    event.preventDefault();
    if (!discountCode) return;

    const body = buildBookingBody(discountCode);

    fetch(`${config.apiUrl}/api/sopotak/v1/bookings/discounts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    })
      .then(handleResponse)
      .then((data) => {
        setDiscountData({
          departureOffer: data.departureOffer,
          returnOffer: data.returnOffer,
        });
      })
      .catch((error) => {
        console.error("Error:", error.message);
        alert(
          error.message === "Discount not found"
            ? t("summary.discountNotFound")
            : error.message === "Discount already used"
            ? t("summary.discountUsed")
            : t("summary.genericError")
        );
        setDiscountData(null);
      });
  };

  const getTotal = () => {
    const total =
      tripType === "RETURN"
        ? parseFloat(departureOffer.price.amount) +
          parseFloat(returnOffer.price.amount)
        : parseFloat(departureOffer.price.amount);
    return total.toFixed(2);
  };

  const getDiscountedPrice = () => {
    const offer = discountData?.departureOffer || departureOffer;
    const returnPart =
      tripType === "RETURN"
        ? parseFloat((discountData?.returnOffer || returnOffer).price.amount)
        : 0;
    return (
      parseFloat(offer.price.amount) + returnPart
    ).toFixed(2);
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const dt = DateTime.fromISO(isoString, { zone: "Europe/Warsaw" }).setLocale(
      i18n.language
    );
    return i18n.language === "en"
      ? dt.toFormat("MM/dd/yyyy")
      : dt.toFormat("dd.MM.yyyy");
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";

    const dt = DateTime.fromISO(isoString, { zone: "Europe/Warsaw" }).setLocale(
      i18n.language
    );

    // AM/PM dla angielskiego
    return i18n.language === "en"
      ? dt.toLocaleString(DateTime.TIME_SIMPLE) // np. "2:30 PM"
      : dt.toLocaleString(DateTime.TIME_24_SIMPLE); // np. "14:30"
  };

  return (
    <div className="form-wrapper">
      <Form noValidate onSubmit={handleSubmit}>
        <Container fluid>
          <Row className="mt-2">
            <Col lg={{ span: 8, offset: 2 }}>
              <h3>{t("summary.route")}</h3>
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={{ span: 8, offset: 2 }}>
              <h5>{t("summary.passengers")}</h5>
              <div className="d-flex justify-content-between">
                <span>{t("summary.adults")}:</span>
                <span>{persons}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>{t("summary.children")}:</span>
                <span>{children}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>{t("summary.bikes")}:</span>
                <span>{bikes}</span>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={{ span: 8, offset: 2 }}>
              <h5>
                {t("summary.direction")}: {from} - {to}
              </h5>
              <div className="d-flex justify-content-between">
                <span>{t("summary.date")}:</span>
                <span>{formatDate(departureOffer.departureTime)}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>{t("summary.time")}:</span>
                <span>{formatTime(departureOffer.departureTime)}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>{t("summary.price")}:</span>
                <span>
                  {departureOffer.price.amount} {departureOffer.price.currency}
                </span>
              </div>
            </Col>
          </Row>

          {tripType === "RETURN" && (
            <Row>
              <Col xs={12} lg={{ span: 8, offset: 2 }}>
                <h5>
                  {t("summary.direction")}: {to} - {from}
                </h5>
                <div className="d-flex justify-content-between">
                  <span>{t("summary.date")}:</span>
                  <span>{formatDate(returnOffer.departureTime)}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>{t("summary.time")}:</span>
                  <span>{formatTime(returnOffer.departureTime)}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>{t("summary.price")}:</span>
                  <span>
                    {returnOffer.price.amount} {returnOffer.price.currency}
                  </span>
                </div>
              </Col>
            </Row>
          )}
          <Row>
            <Col className="mt-2" lg={{ span: 8, offset: 2 }}>
              <h3>{t("summary.total")}</h3>
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={{ span: 8, offset: 2 }}>
              <div className="d-flex justify-content-between">
                <h5>{t("summary.totalLabel")}</h5>
                <span style={{ fontSize: "larger", fontWeight: "bold" }}>
                  {getTotal()} {departureOffer.price.currency}
                </span>
              </div>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col lg={{ span: 8, offset: 2 }}>{t("summary.discountPrompt")}</Col>
          </Row>
          <Row>
            <Col xs={12} lg={{ span: 8, offset: 2 }} className="d-flex">
              <Form.Control
                type="text"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                className="me-2"
              />
              <Button onClick={submitDiscountCodeHandler}>
                {t("summary.applyDiscount")}
              </Button>
            </Col>
          </Row>
          <Row>
            <Col className="mt-3 mb-3" xs={12} lg={{ span: 8, offset: 2 }}>
              <div className="d-flex justify-content-between">
                <h5>{t("summary.toPay")}</h5>
                <span style={{ fontSize: "larger", fontWeight: "bold" }}>
                  {getDiscountedPrice()} {departureOffer.price.currency}
                </span>
              </div>
            </Col>
          </Row>
        </Container>
        <Container>
          <BoatTermsModal
            show={show}
            handleClose={handleClose}
            handleConfirm={handleConfirm}
          />
          <Container fluid>
            <Row>
              <Col className="mt-2 gx-2" lg={{ span: 8, offset: 2 }}>
                <h3>{t("summary.personalData")}</h3>
              </Col>
            </Row>
            <Row>
              <Col className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                <Form.Group>
                  <FloatingLabel
                    controlId="name"
                    label={t("contact.firstName")}
                    style={{ marginBottom: "10px" }}
                  >
                    <Form.Control
                      type="text"
                      value={ctx.firstName}
                      placeholder={t("contact.firstName")}
                      onChange={(e) => ctx.setFirstName(e.target.value)}
                      required
                      isInvalid={validated && ctx.firstName.length < 1}
                    />
                    <Form.Control.Feedback type="invalid">
                      {t("contact.required")}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                <Form.Group>
                  <FloatingLabel
                    controlId="surname"
                    label={t("contact.lastName")}
                  >
                    <Form.Control
                      type="text"
                      value={ctx.lastName}
                      placeholder={t("contact.lastName")}
                      onChange={(e) => ctx.setLastName(e.target.value)}
                      required
                      isInvalid={validated && ctx.lastName.length < 1}
                    />
                    <Form.Control.Feedback type="invalid">
                      {t("contact.required")}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                <Form.Group>
                  <FloatingLabel controlId="email" label={t("contact.email")}>
                    <Form.Control
                      type="text"
                      value={ctx.email}
                      placeholder={t("contact.email")}
                      onChange={(e) => ctx.setEmail(e.target.value)}
                      required
                      isInvalid={validated && !isEmailValid(ctx.email)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {t("contact.invalidEmail")}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col lg={{ span: 8, offset: 2 }}>
                <Row>
                  <Col md={6} className="gx-2">
                    <FloatingLabel label={t("contact.countryCode")}>
                      <CountrySelect
                        labels={labels}
                        value={ctx.countryCode}
                        onChange={handleCountryChange}
                        language={i18n.language}
                        name="countrySelect"
                      />
                    </FloatingLabel>
                  </Col>
                  <Col md={6} className="gx-2">
                    <Form.Group>
                      <FloatingLabel controlId="tel" label={t("contact.phone")}>
                        <Form.Control
                          type="tel"
                          value={ctx.phoneNumber}
                          placeholder={t("contact.phone")}
                          onChange={(e) =>
                            handlePhoneNumberChange(e.target.value)
                          }
                          required
                          isInvalid={validated && !isPhoneNumberValidCustom()}
                          className="custom-input"
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("contact.invalidPhone")}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row>
              <Col className="col-12 gx-2 mb-2" lg={{ span: 8, offset: 2 }}>
                <Form.Check
                  checked={invoice}
                  onChange={handleInvoiceCheckboxChange}
                  label={
                    <Trans
                      i18nKey="contact.invoice"
                      components={{ strong: <strong /> }}
                    />
                  }
                />
              </Col>
            </Row>
            {invoice && (
              <>
                <Row>
                  <Col className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                    <Form.Check
                      inline
                      type="radio"
                      id="invoiceCountryPL"
                      name="invoiceCountry"
                      value="PL"
                      checked={invoiceCountry === "PL"}
                      onChange={handleInvoiceCountryChange}
                      label={t("contact.invoice_pl")}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id="invoiceCountryOther"
                      name="invoiceCountry"
                      value="Other"
                      checked={invoiceCountry === "Other"}
                      onChange={handleInvoiceCountryChange}
                      label={t("contact.invoice_other")}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                    <Form.Group>
                      <FloatingLabel
                        controlId="companyName"
                        label={t("contact.company")}
                        style={{ marginBottom: "10px" }}
                      >
                        <Form.Control
                          type="text"
                          value={ctx.companyName}
                          placeholder={t("contact.company")}
                          onChange={(e) => ctx.setCompanyName(e.target.value)}
                          required={invoice}
                          isInvalid={validated && ctx.companyName.length < 1}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("contact.required")}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>

                {invoiceCountry === "PL" ? (
                  <Row>
                    <Col className="gx-2" lg={{ span: 8, offset: 2 }}>
                      <Form.Group>
                        <FloatingLabel
                          controlId="nipPL"
                          label={t("contact.nip")}
                          style={{ marginBottom: "10px" }}
                        >
                          <Form.Control
                            type="text"
                            value={ctx.nip}
                            placeholder={t("contact.nip")}
                            onChange={(e) => ctx.setNip(e.target.value)}
                            required={invoice}
                            isInvalid={invoice && validated && (!ctx.nip || !isValidNip(ctx.nip))}
                          />
                          <Form.Control.Feedback type="invalid">
                            {!ctx.nip || ctx.nip.trim() === "" ? t("contact.required") : t("contact.nipInvalid")}
                          </Form.Control.Feedback>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                ) : (
                  <Row>
                    <Col className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                      <Form.Group>
                        <FloatingLabel
                          controlId="nipOther"
                          label={t("contact.nip")}
                          style={{ marginBottom: "10px" }}
                        >
                          <Form.Control
                            type="text"
                            value={ctx.nip}
                            placeholder={t("contact.nip")}
                            onChange={(e) => ctx.setNip(e.target.value)}
                            required={invoice}
                            isInvalid={invoice && validated && (!ctx.nip || ctx.nip.trim() === "")}
                          />
                          <Form.Control.Feedback type="invalid">
                            {t("contact.required")}
                          </Form.Control.Feedback>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col className="col-5 gx-2" lg={{ span: 8, offset: 2 }}>
                    <Form.Group>
                      <FloatingLabel
                        controlId="postalCode"
                        label={t("contact.postalCode")}
                        style={{ marginBottom: "10px" }}
                      >
                        <Form.Control
                          type="text"
                          value={ctx.postalCode}
                          placeholder={t("contact.postalCode")}
                          onChange={(e) => ctx.setPostalCode(e.target.value)}
                          required={invoice}
                          isInvalid={
                            validated &&
                            !(
                              ctx.postalCode.length === 5 ||
                              ctx.postalCode.length === 6
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {getPostalCodeErrorMessage()}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col className="col-7 gx-2" lg={{ span: 8, offset: 2 }}>
                    <Form.Group>
                      <FloatingLabel
                        controlId="city"
                        label={t("contact.city")}
                        style={{ marginBottom: "10px" }}
                      >
                        <Form.Control
                          type="text"
                          value={ctx.city}
                          placeholder={t("contact.city")}
                          onChange={(e) => ctx.setCity(e.target.value)}
                          required={invoice}
                          isInvalid={validated && ctx.city.length < 1}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("contact.required")}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col className="col-8 gx-2" lg={{ span: 8, offset: 2 }}>
                    <Form.Group>
                      <FloatingLabel
                        controlId="street"
                        label={t("contact.street")}
                        style={{ marginBottom: "10px" }}
                      >
                        <Form.Control
                          type="text"
                          value={ctx.streetAddress}
                          placeholder={t("contact.street")}
                          onChange={(e) => ctx.setStreetAddress(e.target.value)}
                          required={invoice}
                          isInvalid={validated && ctx.streetAddress.length < 1}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("contact.required")}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                  <Col className="col-4 gx-2" lg={{ span: 8, offset: 2 }}>
                    <Form.Group>
                      <FloatingLabel
                        controlId="apartmentNumber"
                        label={t("contact.apartment")}
                        style={{ marginBottom: "10px" }}
                      >
                        <Form.Control
                          type="text"
                          value={ctx.apartmentNumber}
                          placeholder={t("contact.apartment")}
                          onChange={(e) =>
                            ctx.setApartmentNumber(e.target.value)
                          }
                          required={invoice}
                          isInvalid={validated && ctx.apartmentNumber.length < 1}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("contact.required")}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            <Row>
              <Col className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                <Form.Group>
                  <FloatingLabel
                    controlId="remarks"
                    label={t("contact.comments")}
                    style={{ marginBottom: "10px" }}
                  >
                    <Form.Control
                      as="textarea"
                      value={ctx.comments}
                      placeholder={t("contact.comments")}
                      onChange={(e) => ctx.setComments(e.target.value)}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                <Form.Group>
                  <FloatingLabel
                    controlId="recommendations"
                    label={t("contact.recommend")}
                    style={{ marginBottom: "10px" }}
                  >
                    <Form.Select
                      onChange={recommendationChangeHandler}
                      value={ctx.recommendation}
                    >
                      <option value="" />
                      <option value="Znajomy">
                        {t("contact.options.friend")}
                      </option>
                      <option value="Facebook">
                        {t("contact.options.facebook")}
                      </option>
                      <option value="Instagram">
                        {t("contact.options.instagram")}
                      </option>
                      <option value="Recepcja w hotelu">
                        {t("contact.options.hotel")}
                      </option>
                      <option value="Inne">{t("contact.options.other")}</option>
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                <Form.Check
                  id="approveterms"
                  required
                  label={
                    <Trans
                      i18nKey="contact.approveTerms"
                      components={{
                        a: (
                          <a
                            href={
                              i18n.language === "en"
                                ? "https://cruisemybay.pl/en/terms-and-conditions-of-service/"
                                : "https://cruisemybay.pl/regulamin-serwisu/"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    />
                  }
                  feedback={t("contact.approveTermsFeedback")}
                  feedbackType="invalid"
                  onChange={handleTermsChange}
                  isInvalid={validated && !termsChecked}
                />
              </Col>
            </Row>
            <Row>
              <Col className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                <Form.Check
                  id="approveelectronic"
                  required
                  label={t("contact.approveService")}
                  feedback={t("contact.approveTermsFeedback")}
                  feedbackType="invalid"
                  onChange={(e) => {
                    handleApproveChange(e);
                  }}
                  isInvalid={validated && approvalChecked === false}
                />
              </Col>
            </Row>
            <Row>
              <Col className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                <Form.Check
                  id="approveboatterms"
                  required
                  checked={boatTermsConfirmed}
                  label={
                    <Trans
                      i18nKey="contact.approveBoat"
                      components={{ strong: <strong /> }}
                    />
                  }
                  feedback={t("contact.approveBoatFeedback")}
                  feedbackType="invalid"
                  onChange={handleBoatTermsConfirmedChange}
                  isInvalid={validated && boatTermsConfirmed === false}
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={12} className="col-12 gx-2" lg={{ span: 8, offset: 2 }}>
                <Form.Check
                  id="marketing"
                  checked={ctx.marketingInfoApproved}
                  label={t("contact.marketing")}
                  onChange={(e) => {
                    handleMarketingChange(e);
                  }}
                />
              </Col>
            </Row>
          </Container>
        </Container>
        <Container>
          <Row className="mt-2">
            <Col xs={12} lg={{ span: 8, offset: 2 }}>
              <div className="d-flex">
                <Button
                  size="lg"
                  onClick={navigateToOrderPage}
                  className="w-50 me-2"
                >
                  {t("summary.back")}
                </Button>
                <Button
                  size="lg"
                  className="w-50"
                  type="submit"
                >
                  {t("summary.pay")}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </Form>
    </div>
  );
};

export default SummaryAndContact;